import React, { useState } from 'react';
import TrashIcon from '../icons/TrashIcon';
import {
  MajorRequirement,
  MinorRequirement,
  MajorSummary,
  MinorSummary,
  Requirement,
  RequirementSummary,
} from '../../types';

type RequirementsPanelProps = {
  requirements: Requirement[];
  majors: MajorRequirement[];
  minors: MinorRequirement[];
  requirementSummaries: RequirementSummary[];
  majorSummaries: MajorSummary[];
  minorSummaries: MinorSummary[];
  hasClassRequirement: boolean;
  totalClassesRemaining: number | null;
  totalClassRequirement: number;
  onAddRequirement: () => void;
  onUpdateRequirement: (id: string, field: 'name' | 'count', value: string) => void;
  onRemoveRequirement: (id: string) => void;
  onAddMajor: () => void;
  onUpdateMajorName: (id: string, value: string) => void;
  onUpdateMajorClass: (majorId: string, classId: string, value: string) => void;
  onAddMajorClass: (majorId: string) => void;
  onRemoveMajor: (majorId: string) => void;
  onRemoveMajorClass: (majorId: string, classId: string) => void;
  onAddMinor: () => void;
  onUpdateMinorName: (id: string, value: string) => void;
  onUpdateMinorClass: (minorId: string, classId: string, value: string) => void;
  onAddMinorClass: (minorId: string) => void;
  onRemoveMinor: (minorId: string) => void;
  onRemoveMinorClass: (minorId: string, classId: string) => void;
};

const RequirementsPanel: React.FC<RequirementsPanelProps> = ({
  requirements,
  majors,
  minors,
  requirementSummaries,
  majorSummaries,
  minorSummaries,
  hasClassRequirement,
  totalClassesRemaining,
  totalClassRequirement,
  onAddRequirement,
  onUpdateRequirement,
  onRemoveRequirement,
  onAddMajor,
  onUpdateMajorName,
  onUpdateMajorClass,
  onAddMajorClass,
  onRemoveMajor,
  onRemoveMajorClass,
  onAddMinor,
  onUpdateMinorName,
  onUpdateMinorClass,
  onAddMinorClass,
  onRemoveMinor,
  onRemoveMinorClass,
}) => {
  const [showDistributiveSection, setShowDistributiveSection] = useState(true);
  const [showMajorSection, setShowMajorSection] = useState(true);
  const [showMinorSection, setShowMinorSection] = useState(true);
  const activeCount =
    (showDistributiveSection ? 1 : 0) + (showMajorSection ? 1 : 0) + (showMinorSection ? 1 : 0);
  const gridColsClass =
    activeCount >= 3
      ? 'lg:grid-cols-[repeat(4,minmax(0,1fr))]'
      : activeCount === 2
      ? 'lg:grid-cols-2'
      : 'lg:grid-cols-1';

  return (
  <div className="mt-8 rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-[0_12px_50px_-45px_rgba(15,23,42,0.55)] md:p-6 lg:p-8">
    <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-600">Requirements</h3>
    <p className="mt-1 text-sm text-slate-600">Track totals, distributives, and major/minor plans.</p>
    <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
      <label className="inline-flex items-center gap-2">
        <input
          type="checkbox"
          checked={showDistributiveSection}
          onChange={(event) => setShowDistributiveSection(event.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
        />
        Distributives
      </label>
      <label className="inline-flex items-center gap-2">
        <input
          type="checkbox"
          checked={showMajorSection}
          onChange={(event) => setShowMajorSection(event.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
        />
        Majors
      </label>
      <label className="inline-flex items-center gap-2">
        <input
          type="checkbox"
          checked={showMinorSection}
          onChange={(event) => setShowMinorSection(event.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
        />
        Minors
      </label>
    </div>
    {activeCount === 0 ? (
      <div className="mt-6 rounded-lg border border-dashed border-slate-200 bg-white/70 p-4 text-sm text-slate-600">
        Enable at least one requirement group to view its details.
      </div>
    ) : (
    <div className={`mt-4 grid gap-3 ${gridColsClass}`}>
      {showDistributiveSection && (
      <div
        className="space-y-3"
        style={
          showMajorSection && showMinorSection && activeCount >= 3
            ? { gridColumn: 'span 2 / span 2' }
            : undefined
        }
      >
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white/80 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Distributive requirements</p>
              <p className="text-xs text-slate-600">Add or edit the categories you need to satisfy.</p>
            </div>
            <button
              type="button"
              onClick={onAddRequirement}
              className="rounded-full border border-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-900 transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-22px_rgba(15,23,42,0.55)]"
            >
              Add distributive requirement
            </button>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-700 md:grid-cols-12">
              <span className="md:col-span-7">Distributive</span>
              <span className="text-right md:col-span-5 md:text-left">Required count</span>
            </div>
            {requirements.map((req) => {
              const nameId = `req-name-${req.id}`;
              const countId = `req-count-${req.id}`;
              return (
                <div
                  key={req.id}
                  className="group relative grid gap-3 rounded-lg border border-slate-100 bg-white/70 p-3 md:grid-cols-12"
                >
                  <button
                    type="button"
                    aria-label="Remove distributive requirement"
                    onClick={() => onRemoveRequirement(req.id)}
                    className="absolute -right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition hover:border-rose-200 hover:text-rose-600 hover:shadow-[0_10px_30px_-22px_rgba(248,113,113,0.45)] hover:opacity-100 focus:opacity-100 focus:outline-none group-hover:opacity-100 group-focus-within:opacity-100"
                  >
                    <TrashIcon />
                  </button>
                  <div className="md:col-span-7">
                    <label htmlFor={nameId} className="sr-only">
                      Distributive
                    </label>
                    <input
                      id={nameId}
                      type="text"
                      value={req.name}
                      onChange={(event) => onUpdateRequirement(req.id, 'name', event.target.value)}
                      placeholder=""
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal text-slate-900 shadow-inner shadow-slate-100 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                  </div>
                  <div className="md:col-span-5">
                    <label htmlFor={countId} className="sr-only">
                      Required count
                    </label>
                    <input
                      id={countId}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={req.count}
                      onChange={(event) => onUpdateRequirement(req.id, 'count', event.target.value)}
                      placeholder=""
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal text-slate-900 shadow-inner shadow-slate-100 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {hasClassRequirement ? (
          <div className="rounded-lg border border-slate-200 bg-white/80 p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">Total classes remaining</p>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                {totalClassesRemaining}/{totalClassRequirement} remaining
              </p>
            </div>
            <p className="mt-1 text-xs text-slate-600">Classes left to take.</p>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Enter total classes to track overall progress.</p>
        )}

        {requirementSummaries.length === 0 && (
          <p className="text-sm text-slate-500">Add distributive requirements to track progress.</p>
        )}
        {requirementSummaries.map((item) => (
          <div key={item.name} className="rounded-lg border border-slate-200 bg-white/80 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{item.remaining}/{item.total} remaining</p>
              </div>
            </div>
            <details className="mt-3 space-y-2">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">View fulfillment</summary>
              {item.fulfilled.length === 0 ? (
                <p className="text-sm text-slate-600">No classes assigned yet.</p>
              ) : (
                <ul className="space-y-1 text-sm text-slate-700">
                  {item.fulfilled.map((entry, index) => (
                    <li key={`${item.name}-${index}`}>
                      {entry.course} — {entry.year} {entry.term}
                    </li>
                  ))}
                </ul>
              )}
            </details>
          </div>
        ))}
      </div>
      )}

      {showMajorSection && (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white/60 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Major requirements</p>
            <p className="text-xs text-slate-600">Add majors and the classes they require.</p>
          </div>
          <button
            type="button"
            onClick={onAddMajor}
            className="rounded-full border border-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-900 transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-22px_rgba(15,23,42,0.55)]"
          >
            Add major
          </button>
        </div>
        {majors.length === 0 && <p className="text-xs text-slate-600">No majors added yet.</p>}
        <div className="space-y-3">
          {majors.map((major) => (
            <div key={major.id} className="group relative space-y-2 rounded-lg border border-slate-200 bg-white/80 p-3">
              <button
                type="button"
                aria-label={`Remove ${major.name || 'major'}`}
                onClick={() => onRemoveMajor(major.id)}
                className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition hover:border-rose-200 hover:text-rose-600 hover:shadow-[0_10px_30px_-22px_rgba(248,113,113,0.45)] hover:opacity-100 focus:opacity-100 focus:outline-none group-hover:opacity-100 group-focus-within:opacity-100"
              >
                <TrashIcon />
              </button>
              <input
                type="text"
                value={major.name}
                onChange={(event) => onUpdateMajorName(major.id, event.target.value)}
                placeholder="Major name"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              <div className="space-y-2">
                {major.classes.map((cls) => (
                  <div key={cls.id} className="group/class relative">
                    <input
                      type="text"
                      value={cls.name}
                      onChange={(event) => onUpdateMajorClass(major.id, cls.id, event.target.value)}
                      placeholder={`Class name`}
                      className="w-full rounded-md border border-slate-200 px-3 pr-12 py-2 text-sm font-normal text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                    <button
                      type="button"
                      aria-label="Remove required class"
                      onClick={() => onRemoveMajorClass(major.id, cls.id)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition hover:border-rose-200 hover:text-rose-600 hover:shadow-[0_8px_24px_-16px_rgba(248,113,113,0.45)] hover:opacity-100 focus:opacity-100 focus:outline-none group-hover/class:opacity-100 group-focus-within/class:opacity-100"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onAddMajorClass(major.id)}
                className="rounded-full border border-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-900 transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-22px_rgba(15,23,42,0.55)]"
              >
                Add required class
              </button>
              {majorSummaries
                .filter((summary) => summary.name === (major.name || 'Major'))
                .map((summary) => (
                  <div key={`${summary.name}-summary`} className="rounded-md border border-slate-200 bg-white/70 p-2">
                    <p className="text-xs font-semibold text-slate-900">{summary.remaining}/{summary.total} remaining</p>
                    <details className="mt-1 text-xs">
                      <summary className="cursor-pointer font-semibold uppercase tracking-[0.15em] text-slate-700">View fulfillment</summary>
                      {summary.fulfilled.length === 0 ? (
                        <p className="text-slate-600">No classes assigned yet.</p>
                      ) : (
                        <ul className="mt-1 space-y-1 text-slate-700">
                          {summary.fulfilled.map((entry, index) => (
                            <li key={`${summary.name}-${index}`}>
                              {entry.course} — {entry.year} {entry.term}
                            </li>
                          ))}
                        </ul>
                      )}
                    </details>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
      )}
      {showMinorSection && (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white/60 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Minor requirements</p>
            <p className="text-xs text-slate-600">Keep your minors organized separately.</p>
          </div>
          <button
            type="button"
            onClick={onAddMinor}
            className="rounded-full border border-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-900 transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-22px_rgba(15,23,42,0.55)]"
          >
            Add minor
          </button>
        </div>
        {minors.length === 0 && <p className="text-xs text-slate-600">No minors added yet.</p>}
        <div className="space-y-3">
          {minors.map((minor) => (
            <div key={minor.id} className="group relative space-y-2 rounded-lg border border-slate-200 bg-white/80 p-3">
              <button
                type="button"
                aria-label={`Remove ${minor.name || 'minor'}`}
                onClick={() => onRemoveMinor(minor.id)}
                className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition hover:border-rose-200 hover:text-rose-600 hover:shadow-[0_10px_30px_-22px_rgba(248,113,113,0.45)] hover:opacity-100 focus:opacity-100 focus:outline-none group-hover:opacity-100 group-focus-within:opacity-100"
              >
                <TrashIcon />
              </button>
              <input
                type="text"
                value={minor.name}
                onChange={(event) => onUpdateMinorName(minor.id, event.target.value)}
                placeholder="Minor name"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              <div className="space-y-2">
                {minor.classes.map((cls) => (
                  <div key={cls.id} className="group/class relative">
                    <input
                      type="text"
                      value={cls.name}
                      onChange={(event) => onUpdateMinorClass(minor.id, cls.id, event.target.value)}
                      placeholder="Class name"
                      className="w-full rounded-md border border-slate-200 px-3 pr-12 py-2 text-sm font-normal text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                    <button
                      type="button"
                      aria-label="Remove required class"
                      onClick={() => onRemoveMinorClass(minor.id, cls.id)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition hover:border-rose-200 hover:text-rose-600 hover:shadow-[0_8px_24px_-16px_rgba(248,113,113,0.45)] hover:opacity-100 focus:opacity-100 focus:outline-none group-hover/class:opacity-100 group-focus-within/class:opacity-100"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onAddMinorClass(minor.id)}
                className="rounded-full border border-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-900 transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-22px_rgba(15,23,42,0.55)]"
              >
                Add required class
              </button>
              {minorSummaries
                .filter((summary) => summary.name === (minor.name || 'Minor'))
                .map((summary) => (
                  <div key={`${summary.name}-summary`} className="rounded-md border border-slate-200 bg-white/70 p-2">
                    <p className="text-xs font-semibold text-slate-900">
                      {summary.remaining}/{summary.total} remaining
                    </p>
                    <details className="mt-1 text-xs">
                      <summary className="cursor-pointer font-semibold uppercase tracking-[0.15em] text-slate-700">View fulfillment</summary>
                      {summary.fulfilled.length === 0 ? (
                        <p className="text-slate-600">No classes assigned yet.</p>
                      ) : (
                        <ul className="mt-1 space-y-1 text-slate-700">
                          {summary.fulfilled.map((entry, index) => (
                            <li key={`${summary.name}-${index}`}>
                              {entry.course} — {entry.year} {entry.term}
                            </li>
                          ))}
                        </ul>
                      )}
                    </details>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
    )}
  </div>
);
};

export default RequirementsPanel;
